import { AbstractControl, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { CustomFile } from '../models/custom.file.model';

export class CustomValidators {
    public static specialCharValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        let newText = control.value.replace(/[`~!@#$%^?&*(){}_|+-=;:'",.<>\{\}\[\]\\\/]/gi, '');
        if (!newText || (newText && newText == '')) {
            return { specialchar: true };
        }
        return null;
    }

    public static specialCharWithoutNumbersValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        // NOTE: check for special char event if there space between them
        const regex = /[`~!@#$%^&*§±()_+\-=\[\]{};':"\\|,.<>\/?]\s*/gi;
        let newText = control.value.trim().replace(regex, '');

        if (!newText || (newText && newText == '')) {
            return { specialchar: true };
        }
        return null;
    }

    public static noWhitespaceValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        const isWhitespace = (control.value || '').trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { whitespace: true };
    }
    public static noWhitespaceValueValidator(control: FormControl | any) {
        if (control.value != '') {
            return null;
        }
        const isWhitespace = (control.value || '').trim().length === 0;
        const isValid = !isWhitespace;
        return isValid ? null : { whitespacevalue: true };
    }
    public static noNumbersOnly(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        const numbersOnlyRegex: RegExp = /^[0-9]*$/;
        if (control.value && numbersOnlyRegex.test(control.value)) {
            return { numbersOnly: true };
        }
        return null;
    }

    public static urlValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        const pattern = '^(http|https)://[^ "]+$';
        const regex = new RegExp(pattern);
        return regex.test(control.value) ? null : { invalidUrl: true };
    }

    public static phoneNumberValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        const pattern = '^009665[0-9]{8}$';
        const regex = new RegExp(pattern);
        return regex.test(control.value) ? null : { invalidPhoneNumber: true };
    }
    public static phoneNumberTicketingValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        let d = control.value.charAt(0);
        const pattern = '^[0-9]+$';
        const regex = new RegExp(pattern);
        return regex.test(control.value) && control.value.length >= 9 && d === '5' ? null : { invalidPhoneNumberTicketing: true };
    }
    public static textMessageTicketingValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        let d = control.value.charAt(0);
        const pattern = '[^A-Za-z0-9]';
        const regex = new RegExp(pattern);
        return regex.test(control.value) && control.value.length <= 899 ? null : { invalidTextTicketing: true };
    }

    public static dateValidator(startDateControlName: string, ennDateControlName: string): ValidatorFn {
        return (controls: AbstractControl) => {
            const startDateControl = controls.get(startDateControlName);
            const endDateControl = controls.get(ennDateControlName);

            if (!startDateControl || !endDateControl || !startDateControl.value || !endDateControl.value) {
                return null;
            }

            const startDate = new Date(startDateControl.value);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(endDateControl.value);
            endDate.setHours(0, 0, 0, 0);

            if (startDate <= endDate) {
                startDateControl.errors;
                if (startDateControl.hasError('invalidDate')) {
                    startDateControl.setErrors(null);
                }
                if (endDateControl.hasError('invalidDate')) {
                    endDateControl.setErrors(null);
                }
                return null;
            }
            startDateControl?.setErrors({ invalidDate: true });
            endDateControl?.setErrors({ invalidDate: true });
            return { invalidDate: true };
        };
    }

    public static endDateValidator(endDateControl: FormControl | any) {
        if (!endDateControl.value) {
            return null;
        }

        const endDate = new Date(endDateControl.value);
        endDate.setHours(0, 0, 0, 0);
        const todaysDate = new Date();
        todaysDate.setHours(0, 0, 0, 0);

        if (todaysDate <= endDate) {
            if (endDateControl.hasError('invalidEndDate')) {
                endDateControl.setErrors(null);
            }
            return null;
        }
        endDateControl?.setErrors({ invalidEndDate: true });
        return { invalidEndDate: true };
    }

    // max size in KB
    static fileSize(maxFileSize: number): ValidatorFn {
        const validatorFn = (fileObject: any) => {
            if (fileObject instanceof File || fileObject instanceof CustomFile) {
                let currentFile: File = fileObject instanceof File ? fileObject : fileObject.file;
                if (!currentFile) {
                    return null;
                }
                if (Math.round(currentFile.size / 1024) > maxFileSize) {
                    // 0.5 MB
                    return { invalidSize: true };
                }
            }
            return null;
        };
        return CustomValidators.fileValidation(validatorFn);
    }

    public static fileExtensions(allowedExtensions: Array<string>): ValidatorFn {
        const validatorFn = (fileObject: any) => {
            if (allowedExtensions.length === 0) {
                return null;
            }
            if (fileObject instanceof File || fileObject instanceof CustomFile) {
                let currentFile = fileObject instanceof File ? fileObject : fileObject.file;
                if (!currentFile) {
                    return null;
                }
                const ext: string = CustomValidators.getExtension(currentFile.name)!;
                if (allowedExtensions.indexOf(ext) === -1) {
                    return {
                        fileExtension: {
                            allowedExtensions,
                            actualExtension: currentFile.type,
                            file: fileObject
                        }
                    };
                }
            }
            return null;
        };
        return CustomValidators.fileValidation(validatorFn);
    }

    private static getExtension(filename: string): null | string {
        filename = filename!;
        if (filename && filename.indexOf('.') === -1) {
            return null;
        }
        return filename?.split('.')?.pop()?.toLowerCase() ?? null;
    }

    private static fileValidation(validatorFn: (file: any) => null | any): ValidatorFn {
        return (formControl: any) => {
            if (!formControl && !formControl.value) {
                return null;
            }

            const files: any[] = [];
            const isMultiple = Array.isArray(formControl.value);
            isMultiple ? formControl.value.forEach((file: any) => files.push(file)) : files.push(formControl.value);

            for (const file of files) {
                return validatorFn(file);
            }

            return null;
        };
    }

    public static validateFile(file: any, maxFileSize: number, allowedExtensions: string[], excludedExtensions?: string[]) {
        const ext: string = CustomValidators.getExtension(file.name)!;
        if (
            (excludedExtensions && excludedExtensions.length && excludedExtensions.indexOf(ext) > -1) ||
            (allowedExtensions && allowedExtensions.length && allowedExtensions.indexOf(ext) === -1)
        ) {
            file.errorMessage = 'fileSupported';
            return false;
        }
        if (file.size > maxFileSize) {
            file.errorMessage = 'MaximumFileSizeWarning';
            return false;
        }
        return true;
    }

    public static emailValidator(control: FormControl) {
        if (!control.value) {
            return null; // Don't validate if the field is empty
        }

        const email = control.value.trim();

        // Regex to check the basic structure of the email
        const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;


        // Check if it matches the basic pattern (local + domain + TLD)
        if (!pattern.test(email)) {
            return { invalidEmail: true }; // Invalid email structure
        }

        // Check for consecutive dots in the email
        if (/\.{2,}/.test(email)) {
            return { invalidEmail: true }; // Invalid email due to consecutive dots
        }

        // Split email into local part and domain part
        const [local, domain] = email.split('@');

        // Check if the local part starts or ends with a dot
        if (local.startsWith('.') || local.endsWith('.')) {
            return { invalidEmail: true }; // Local part starts or ends with a dot
        }

        // Check if the domain part starts or ends with a hyphen or dot
        if (domain.startsWith('-') || domain.endsWith('-') || domain.startsWith('.') || domain.endsWith('.')) {
            return { invalidEmail: true }; // Domain part starts or ends with a hyphen or dot
        }

        return null; // Valid email
    }
    public static emailSTCValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        const pattern = /^[\w-\.]+@(stc.com.sa)$/;
        const regex = new RegExp(pattern);
        return regex.test(control.value.trim()) ? null : { invalidEmailSTC: true };
    }
    public static atLeastOneInputHasValueValidator(form: FormGroup) {
        let controls = form.controls;
        return Object.keys(controls).some(key => controls[key].value !== '') ? null : { atLeastOneRequired: true };
    }

    static checkWhiteSpaces(control: FormControl) {
        const text = control.value;
        if (!text) return null;
        return text.replace(/\s/g, '') ? null : { whiteSpace: true };
    }

    static checkForBothNumbersAndSpecialChars(control: FormControl) {
        const text = control.value;
        if (!text) return null;
        const regex = /^(?=.*?[0-9\u0660-\u0669])(?=.*?[$`~&+,:;=?@#§±"|'<>.^*(){}\[\]\/\\_%!-])[0-9\u0660-\u0669 $`~&+,:;=?@#§±"|'<>.^*(){}\[\]\/\\_%!-]+$/gi;
        return text.replace(regex, '') ? null : { numbersAndSpecialChars: true };
    }
    static checkForNumbersAndHyphen(control: FormControl) {
        const text = control.value;
        if (!text) return null;
        const regex = /^(?=.*[0-9])[0-9-]+$/gi;

        return  regex.test(text) ? null : { numbersAndAndHyphen: true };
    }
    public static checkForNumbersAndCharacters(control: FormControl) {
        const text = control.value;
        if (!text) return null;
        const regex = /^(?=.*[0-9])(?=.*[a-zA-Z])[a-zA-Z0-9]+$/gi;
        return text.replace(regex, '') ? null : { numbersAndSpecialChars: true };
    }

    static checkWhiteSpaceBetweenString(control: FormControl) {
        const text = control.value;
        if (!text) return null;
        return text.trim().search(/\s/) === -1 ? null : { whitespaceBetween: true };
    }

    static checkForEnglishAndArabicLetters(control) {
        const text = control.value;
        if (!text) return null;
        const regex = /^[a-zA-Z ء-ي]+$/;
        return text.replace(regex, '') ? null : { englishOrArabicLetters: true };
    }
    static checkForOnlyEnglishAndArabicLetters(control) {
        const text = control.value;
        if (!text) return null;
        const regex = /^[a-zA-Z\u0600-\u06FF\s]+$/;
        return regex.test(text) ? null : { englishOrArabicLettersOnly: true };
    }
 
    static validLatitudeAndLongitude(control: FormControl) {
        const text = control.value;
        if (!text) return null;

        const latAndLongDecimalDegrees = /^[+-]?(([1-8]?[1-9]|[1-9]0)([\.]{1}\d{1,})?)([\,]?\s([+-]?(([1-8]?[1-9]|[1-9]0)([\.]{1}\d{1,}))))$/gi;
        const latAndLongDegreesMinSec =
            /(^[+-]?((([1-8]?[0-9])[\s])(([1-6]?[0-9])[\s])(([0-5]\d|\d)([.|,]\d{1,20})?[,]?)))(([\,]?\s(([1-8]?[0-9])[\s])(([1-6]?[0-9])[\s]\s*)(([0-6]\d|\d)([.|,]\d{1,20})?\s*)))$/gi;

        const isDecimalDegrees = text.trim().replace(latAndLongDecimalDegrees, '') ? { DD: false } : null;
        const isDegreesMinSec = text.trim().replace(latAndLongDegreesMinSec, '') ? { DMS: false } : null;

        if (isDecimalDegrees && !isDecimalDegrees.DD && isDegreesMinSec && !isDegreesMinSec.DMS) {
            return { latAndLong: false };
        } else if (isDecimalDegrees || isDegreesMinSec) {
            return null;
        } else {
            return null;
        }
    }

    static only3LettersAnd3NumbersValidator(control: FormControl) {
        const text = control.value;
        if (!text) return null;
        const regex = /^(?=(.*[A-Za-z]){3})(?=(.*\d){3})[A-Za-z\d]{6}$/gi;
        return text.trim().replace(regex, '') ? { '3LettersAndNumbers': false } : null;
    }
    static only3LettersValidator(control: FormControl) {
        const text = control.value;
        if (!text) return null;
        const pattern = /^[A-Z]{3}$/;
        const regex = new RegExp(pattern);
        return regex.test(control.value.trim()) ? null : { Letters3: true };
    }
    /**  Check for both English and Arabic alphabetic and numbers */
    static onlyLettersAndNumbers(control) {
        const text = control.value;
        if (!text) return null;
        const regex = /^(?=.*?[0-9\u0660-\u0669])(?=.*?[a-zA-Zء-ي])[a-zA-Z0-9 \u0660-\u0669 ء-ي]+$/gi;
        return text.replace(regex, '') ? { lettersAndNumber: false } : null;
    }

    /** Only valid if value is an ip address */
    static ipAddressValidator(control: FormControl) {
        const text = control.value;
        const regex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!text) return null;
        return text.trim().replace(regex, '') ? { ipAddressError: false } : null;
    }

    /**  Check for both English and Arabic alphabetic, numbers and special chars */
    static checkForBothLettersAndSpecialChars(control) {
        const text = control.value;
        if (!text) return null;
        const regex = /^(?=.*?[$`~&+,:;=?@#"|'<>.^*(){}\[\]\/\\_%!-])(?=.*?[a-zA-Z ء-ي])[a-zA-Zء-ي$`~&+,:;=?@#"|'<>.^*(){}\[\]\/\\_%!-]+$/gi;
        return text.replace(regex, '') ? null : { lettersAndSpecialChars: true };
    }

    public static phoneNumberwithoutLenghtValidator(control: FormControl | any) {
        if (!control.value) {
            return null;
        }
        const pattern = '^[+0-9]+$';
        const regex = new RegExp(pattern);
        return regex.test(control.value) ? null : { invalidPhoneNumberwithoutLenght: true };
    }
}
